/* ============================================================================
   VILLAINS TCG — Collection body (cst-collection.js)
   Progressive enhancement over a native Search & Discovery GET form:
   • Filter + Sort drawers (focus-trapped, ESC, scrim, scroll-lock, restore focus)
   • Grid switcher (2/3/4 columns) persisted in localStorage
   • Sort select / sort-drawer options submit the facet form (keeps active filters)
   • Expandable collection description (Read more / less)
   Filtering, sorting and pagination all work with JS disabled (real links/submit).
   Re-inits on shopify:section:load for the Theme Editor.
   ========================================================================== */
(() => {
  const COLS_KEY = 'cst:cols';

  /* ------------------------------------------ sticky toolbar offset (header) */
  /* The sticky toolbar must sit below the theme's sticky header bar, which shares
     the same scroll container. Measure it (and keep it live) rather than guessing. */
  let offsetBound = false;
  const setToolbarOffset = () => {
    const bar = document.querySelector('.cst-header__bar');
    const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--cst-toolbar-top', (h || 60) + 'px');
  };
  const bindToolbarOffset = () => {
    if (offsetBound) return;
    offsetBound = true;
    setToolbarOffset();
    window.addEventListener('resize', setToolbarOffset, { passive: true });
    const bar = document.querySelector('.cst-header__bar');
    if (bar && window.ResizeObserver) new ResizeObserver(setToolbarOffset).observe(bar);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(setToolbarOffset);
  };

  /* ------------------------------------------------------------------ drawers */
  const focusable = (el) =>
    el.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea,[tabindex]:not([tabindex="-1"])');

  /* ESC + focus-trap for whichever drawer is open — bound ONCE at document level
     (resolves the live open drawer each keypress → no per-section leak on re-render). */
  let keydownBound = false;
  const bindGlobalKeydown = () => {
    if (keydownBound) return;
    keydownBound = true;
    document.addEventListener('keydown', (e) => {
      const drawer = document.querySelector('[data-cst-drawer].is-open');
      if (!drawer) return;
      if (e.key === 'Escape') { const c = drawer.querySelector('[data-cst-close]'); if (c) c.click(); return; }
      if (e.key === 'Tab') {
        const f = [...focusable(drawer)].filter((n) => n.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  };

  const bindDrawers = (root) => {
    let opener = null;

    const open = (drawer) => {
      if (!drawer) return;
      opener = document.activeElement;
      drawer.removeAttribute('hidden');
      // next frame → transition in
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.documentElement.classList.add('cst-scroll-lock');
      const btn = drawer.querySelector('.cst-drawer2__close') || focusable(drawer)[0];
      if (btn) btn.focus();
      const trigger = root.querySelector(`[data-cst-open][aria-controls="${drawer.id}"]`);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    };

    const close = (drawer) => {
      if (!drawer || drawer.hasAttribute('hidden')) return;
      drawer.classList.remove('is-open');
      const done = () => { drawer.setAttribute('hidden', ''); drawer.removeEventListener('transitionend', done); };
      drawer.addEventListener('transitionend', done);
      setTimeout(done, 400); // fallback if no transition
      document.documentElement.classList.remove('cst-scroll-lock');
      const trigger = root.querySelector(`[data-cst-open][aria-controls="${drawer.id}"]`);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (opener && opener.focus) opener.focus();
    };

    root.querySelectorAll('[data-cst-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const which = btn.dataset.cstOpen;
        open(root.querySelector(`[data-cst-drawer="${which}"]`));
      });
    });
    root.querySelectorAll('[data-cst-close]').forEach((el) => {
      el.addEventListener('click', () => close(el.closest('[data-cst-drawer]')));
    });
  };

  /* ------------------------------------------------------------ grid switcher */
  const bindGridSwitch = (root) => {
    const group = root.querySelector('[data-cst-grid-switch]');
    const grid = root.querySelector('[data-cst-grid]');
    if (!group || !grid) return;

    const apply = (cols, persist) => {
      grid.dataset.cols = cols;
      group.querySelectorAll('[data-cols]').forEach((b) => {
        const on = b.dataset.cols === cols;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (persist) { try { localStorage.setItem(COLS_KEY, cols); } catch (_) {} }
    };

    // restore preference (only if it's an offered option on this viewport)
    let saved = null;
    try { saved = localStorage.getItem(COLS_KEY); } catch (_) {}
    if (saved && group.querySelector(`[data-cols="${saved}"]`)) apply(saved, false);

    group.querySelectorAll('[data-cols]').forEach((b) => {
      b.addEventListener('click', () => apply(b.dataset.cols, true));
    });
  };

  /* -------------------------------------------------------------------- sort */
  const bindSort = (root) => {
    const form = root.querySelector('[data-cst-facet-form]');
    const select = root.querySelector('[data-cst-sort]');
    if (select && form) {
      select.addEventListener('change', () => {
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
      });
    }
    // mobile sort drawer → set the select + submit
    root.querySelectorAll('[data-cst-sort-list] [data-sort-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (select) { select.value = btn.dataset.sortValue; }
        if (form) { form.requestSubmit ? form.requestSubmit() : form.submit(); }
      });
    });
  };

  /* ------------------------------------------------------- expandable description */
  const bindDesc = (root) => {
    const wrap = root.querySelector('[data-cst-desc]');
    if (!wrap) return;
    const body = wrap.querySelector('[data-cst-desc-body]');
    const toggle = wrap.querySelector('[data-cst-desc-toggle]');
    if (!body || !toggle) return;
    // activate the clamp only now that JS runs (no-JS shows the full description)
    wrap.classList.add('cst-col-desc--js');
    // only offer the toggle if the content actually overflows the clamped height
    const clampPx = parseFloat(getComputedStyle(body).maxHeight) || 0;
    if (body.scrollHeight <= clampPx + 4) { toggle.hidden = true; wrap.classList.add('is-short'); return; }
    toggle.addEventListener('click', () => {
      const open = wrap.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  const init = (root) => {
    const scope = root && root.querySelector ? root : document;
    const sections = scope.querySelectorAll('[data-cst-collection]');
    if (sections.length) { bindToolbarOffset(); bindGlobalKeydown(); }
    sections.forEach((sec) => {
      if (sec.dataset.cstBound) return;
      sec.dataset.cstBound = '1';
      bindDrawers(sec);
      bindGridSwitch(sec);
      bindSort(sec);
      bindDesc(sec);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target || document));
})();
